import React, { useState, useEffect, useRef } from 'react';
import { HeroContainer, WavyBackground, ContentContainer, Title, Subtitle } from '../components/HeroSectionStyles';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ParticlesBackground from '../components/ParticlesBackground';
import { FaCheckCircle } from 'react-icons/fa';
import HoloInput from '../components/HoloInput';
import { createGlobalStyle } from 'styled-components';

const NoScanline = createGlobalStyle`
  .link-no-scanline::before {
    display: none !important;
  }
`;

const Form = styled.form`
  background: rgba(10, 25, 47, 0.88);
  padding: 2.8rem 2.2rem 2.5rem 2.2rem;
  border-radius: 22px;
  box-shadow: 0 6px 48px 0 #64B6FF22, 0 0 0 2px #64B6FF22;
  max-width: 420px;
  margin: 32px auto 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  z-index: 2;
  align-items: stretch;
  position: relative;
  @media (max-width: 600px) {
    padding: 1.4rem 0.5rem 1.2rem 0.5rem;
    max-width: 98vw;
  }
`;

const Button = styled.button`
  background: linear-gradient(90deg, #3fd0ff 0%, #4C6EF5 100%);
  color: #fff;
  padding: 1.2rem 1.4rem;
  border: none;
  border-radius: 16px;
  font-size: 1.25rem;
  font-family: 'Orbitron', 'Montserrat', 'Segoe UI', Arial, sans-serif;
  font-weight: 800;
  letter-spacing: 0.04em;
  cursor: pointer;
  box-shadow: 0 2px 24px 0 #4C6EF599, 0 0 0 2px #00ffc844;
  margin-top: 0.5rem;
  transition: background 0.25s, box-shadow 0.22s, filter 0.2s, transform 0.18s;
  text-shadow: 0 0 12px #00ffc855, 0 0 24px #4C6EF577;
  position: relative;
  overflow: hidden;
  outline: none;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, #00ffc8 0%, #4C6EF5 100%);
    opacity: 0.18;
    filter: blur(8px);
    z-index: 0;
    transition: opacity 0.3s;
  }

  &:hover, &:focus {
    background: linear-gradient(90deg, #4C6EF5 0%, #3fd0ff 100%);
    box-shadow: 0 6px 40px 0 #00ffc888;
    filter: brightness(1.13) saturate(1.2);
    transform: translateY(-2px) scale(1.025);
    &::before {
      opacity: 0.32;
    }
    outline: none !important;
    border: none !important;
  }

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 180%;
    height: 180%;
    pointer-events: none;
    background: radial-gradient(circle, #00ffc822 0%, transparent 60%);
    opacity: 0;
    transform: translate(-50%, -50%) scale(1);
    transition: opacity 0.33s, transform 0.33s;
    z-index: 0;
  }

  &:active {
    filter: brightness(0.98);
    transform: scale(0.97);
    &::after {
      opacity: 0.7;
      transform: translate(-50%, -50%) scale(1.08);
    }
  }

  @media (max-width: 600px) {
    font-size: 1.07rem;
    padding: 1rem 0.7rem;
  }
`;

const TechLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2.2rem 0 1.2rem 0;
  position: relative;
  .tech-canvas {
    width: 88px;
    height: 88px;
    background: radial-gradient(circle at 60% 40%, #64B6FF44 40%, transparent 100%);
    border-radius: 50%;
    position: relative;
    overflow: visible;
    box-shadow: 0 0 48px #64B6FF88, 0 0 0 2px #4C6EF544;
    animation: glowPulse 1.8s infinite alternate;
  }
  .scanline {
    position: absolute;
    left: 0;
    top: 50%;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, transparent, #00ffc8cc 60%, transparent);
    filter: blur(1px);
    animation: scanMove 1.2s linear infinite;
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }
  .tech-particles {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  @keyframes scanMove {
    0% { top: 0%; }
    100% { top: 100%; }
  }
  @keyframes glowPulse {
    0% { box-shadow: 0 0 32px #64B6FF88, 0 0 0 2px #4C6EF544; }
    100% { box-shadow: 0 0 64px #00ffc888, 0 0 0 4px #00ffc866; }
  }
  .loader-msg {
    color: #64B6FF;
    font-weight: 600;
    margin-top: 18px;
    font-size: 1.13rem;
    letter-spacing: 0.03em;
    text-shadow: 0 0 8px #64B6FF77;
  }
  .progress-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #64B6FF;
    font-weight: 700;
    font-size: 1.5rem;
    text-shadow: 0 0 8px #64B6FF77;
    z-index: 2;
  }
`;

const SuccessExplosion = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2.2rem 0 1.2rem 0;
  position: relative;
  .explosion {
    position: relative;
    width: 92px;
    height: 92px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }
  .explosion-svg {
    position: absolute;
    width: 120px;
    height: 120px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1;
    filter: blur(0.5px) brightness(1.5);
    opacity: 0.85;
  }
  .success-icon {
    font-size: 3.6rem;
    color: #00ffc8;
    filter: drop-shadow(0 0 32px #00ffc8cc) brightness(1.2);
    animation: iconPop 0.7s cubic-bezier(.68,-0.55,.27,1.55) 1;
    z-index: 2;
  }
  @keyframes iconPop {
    0% { transform: scale(0.2); opacity: 0; }
    60% { transform: scale(1.3); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  .success-msg {
    background: linear-gradient(90deg,#00ffc8,#64B6FF,#4C6EF5 80%);
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
    font-weight: 700;
    margin-top: 0.9rem;
    font-size: 1.28rem;
    letter-spacing: 0.045em;
    text-shadow: 0 0 12px #00ffc855, 0 0 32px #4C6EF555;
    animation: shimmerText 2s linear infinite;
    text-align: center;
    line-height: 1.3;
  }
  @keyframes shimmerText {
    0% { background-position: 0% 50%; }
    100% { background-position: 100% 50%; }
  }
`;

const ErrorExplosion = styled(SuccessExplosion)`
  .explosion-svg {
    filter: blur(0.5px) brightness(1.5);
    opacity: 0.85;
    stroke: #F22F46;
  }
  .success-icon {
    color: #F22F46;
    filter: drop-shadow(0 0 12px #F22F46) brightness(1.2);
  }
  .success-msg {
    background: linear-gradient(90deg,#F22F46,#F22F46,#F22F46 80%);
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
    font-weight: 700;
    margin-top: 0.9rem;
    font-size: 1.28rem;
    letter-spacing: 0.045em;
    text-shadow: 0 0 12px #F22F46, 0 0 32px #F22F46;
    animation: shimmerText 2s linear infinite;
    text-align: center;
    line-height: 1.3;
  }
`;

const ModalOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse at 60% 40%, #162040e0 50%, #141a28cc 100%);
  backdrop-filter: blur(7px) saturate(1.4);
  border-radius: 22px;
  animation: overlayFadeIn 0.4s;
  pointer-events: all;
  @keyframes overlayFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const TechLoaderBig = styled(TechLoader)`
  .tech-canvas {
    width: 188px;
    height: 188px;
    box-shadow: 0 0 96px #64B6FFcc, 0 0 0 4px #4C6EF577;
  }
  .loader-msg {
    font-size: 1.45rem;
    margin-top: 32px;
    letter-spacing: 0.08em;
    text-shadow: 0 0 16px #64B6FFcc, 0 0 32px #00ffc855;
  }
`;
const SuccessExplosionBig = styled(SuccessExplosion)`
  .explosion {
    width: 188px;
    height: 188px;
  }
  .explosion-svg {
    width: 250px;
    height: 250px;
    filter: blur(1.2px) brightness(2.2);
  }
  .success-icon {
    font-size: 5.2rem;
    filter: drop-shadow(0 0 48px #00ffc8cc) brightness(1.3);
  }
  .success-msg {
    font-size: 1.7rem;
    margin-top: 1.6rem;
    letter-spacing: 0.09em;
    text-shadow: 0 0 24px #00ffc8bb, 0 0 48px #4C6EF5aa;
  }
`;
const ErrorExplosionBig = styled(ErrorExplosion)`
  .explosion {
    width: 188px;
    height: 188px;
  }
  .explosion-svg {
    width: 250px;
    height: 250px;
    filter: blur(1.2px) brightness(2.2);
  }
  .success-icon {
    font-size: 5.2rem;
    filter: drop-shadow(0 0 48px #F22F46) brightness(1.3);
  }
  .success-msg {
    font-size: 1.7rem;
    margin-top: 1.6rem;
    letter-spacing: 0.09em;
    text-shadow: 0 0 24px #F22F46, 0 0 48px #F22F46;
  }
`;

export default function LinkFormPage() {
  const [form, setForm] = useState({ nome: '', telefone: '', empresa: '', link: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const progressInterval = useRef();
  const navigate = useNavigate();

  // Função para formatar telefone para (11) 98765-4321
  function formatPhoneInput(value) {
    let digits = value.replace(/\D/g, '');
    if (digits.length > 11) digits = digits.slice(0, 11);
    if (digits.length > 2) {
      let ddd = digits.slice(0, 2);
      let rest = digits.slice(2);
      if (rest.length > 5) {
        return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5, 9)}`;
      } else if (rest.length > 0) {
        return `(${ddd}) ${rest}`;
      } else {
        return `(${ddd}`;
      }
    } else {
      return digits;
    }
  }
  // Função para remover formatação e enviar apenas números
  function toNumericPhone(value) {
    return value.replace(/\D/g, '');
  }

  useEffect(() => {
    if (loading) {
      setProgress(0);
      let start = Date.now();
      progressInterval.current = setInterval(() => {
        let elapsed = (Date.now() - start) / 1000;
        let percent = Math.min(100, Math.round((elapsed / 30) * 100));
        setProgress(percent);
        if (percent >= 100) clearInterval(progressInterval.current);
      }, 250);
    } else {
      setProgress(0);
      clearInterval(progressInterval.current);
    }
    return () => clearInterval(progressInterval.current);
  }, [loading]);

  const handleChange = (e) => {
    if (e.target.name === 'telefone') {
      setForm({ ...form, telefone: formatPhoneInput(e.target.value) });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const payload = { ...form, telefone: toNumericPhone(form.telefone) };
      const response = await fetch('https://webhook-editor.abianca.com.br/webhook/extrai-link-44d5-878f-2ef02f450ced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const text = await response.text();
      if (text.trim().toLowerCase().includes('sucesso')) {
        setSuccess(true);
        setForm({ nome: '', telefone: '', empresa: '', link: '' });
      } else {
        setError('Não foi possível processar o link. Por favor, confira as informações e tente novamente.');
      }
    } catch (err) {
      setError('Erro ao enviar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <HeroContainer className="link-no-scanline">
      <NoScanline />
      <ParticlesBackground />
      <WavyBackground />
      <ContentContainer style={{ zIndex: 2 }}>
        <Title style={{ fontWeight: 800, letterSpacing: '0.04em', fontSize: '2.4rem', marginBottom: 0 }}>
          <div className="logo-container">
            <div className="logo-tech-circle"></div>
            <div className="logo-tech-data"></div>
            <img src="/favicon.png" alt="Logo Bianca" className="title-logo" />
          </div>
          <div className="title-text-container">
            <div className="tech-data-top">// AI.AGENT</div>
            <span className="title-text">BIANCA</span>
            <div className="tech-data-bottom">{/* data.transform(leads) */}</div>
          </div>
        </Title>
        <Subtitle style={{ marginBottom: 28, fontSize: '1.18rem', color: '#90CAFF', fontWeight: 500 }}>
          A Bianca vai analisar o link do imóvel e simular uma ligação de agendamento com você!
        </Subtitle>
        <Form onSubmit={handleSubmit} autoComplete="off" style={{ pointerEvents: (loading || success || error) ? 'none' : undefined }}>
          <HoloInput
            label="Nome"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            required
            autoFocus
          />
          <HoloInput
            label="Telefone"
            name="telefone"
            value={form.telefone}
            onChange={handleChange}
            required
            maxLength={16}
            pattern="\\(\\d{2}\\) \\d{5}-\\d{4}"
          />
          <HoloInput
            label="Nome da empresa"
            name="empresa"
            value={form.empresa}
            onChange={handleChange}
            required
          />
          <HoloInput
            label="Link do imóvel"
            name="link"
            value={form.link}
            onChange={handleChange}
            required
            type="url"
            pattern="https?://.+"
          />
          <Button type="submit" disabled={loading}>
            Enviar
          </Button>
          {error && <span style={{ color: '#F22F46', marginTop: 8 }}>{error}</span>}
          {(loading || success || error) && (
            <ModalOverlay>
              {loading ? (
                <TechLoaderBig>
                  <div className="tech-canvas">
                    <div className="scanline" />
                    <div className="tech-particles" />
                    <div className="progress-text">{progress}%</div>
                  </div>
                  <div className="loader-msg">Analisando link...</div>
                </TechLoaderBig>
              ) : success ? (
                <SuccessExplosionBig>
                  <div className="explosion">
                    <svg className="explosion-svg" viewBox="0 0 250 250">
                      <g>
                        <circle cx="125" cy="125" r="90" stroke="#00ffc8" strokeWidth="4" fill="none" opacity="0.4">
                          <animate attributeName="r" from="40" to="90" dur="0.7s" fill="freeze" />
                          <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                        </circle>
                        <circle cx="125" cy="125" r="60" stroke="#64B6FF" strokeWidth="2.8" fill="none" opacity="0.3">
                          <animate attributeName="r" from="18" to="60" dur="1s" fill="freeze" />
                          <animate attributeName="opacity" from="1" to="0.2" dur="1.2s" fill="freeze" />
                        </circle>
                        <g>
                          <line x1="125" y1="125" x2="125" y2="30" stroke="#00ffc8" strokeWidth="3.5" opacity="0.7">
                            <animate attributeName="y2" from="125" to="30" dur="0.7s" fill="freeze" />
                            <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                          </line>
                          <line x1="125" y1="125" x2="220" y2="125" stroke="#64B6FF" strokeWidth="3.5" opacity="0.7">
                            <animate attributeName="x2" from="125" to="220" dur="0.7s" fill="freeze" />
                            <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                          </line>
                          <line x1="125" y1="125" x2="125" y2="220" stroke="#4C6EF5" strokeWidth="3.5" opacity="0.7">
                            <animate attributeName="y2" from="125" to="220" dur="0.7s" fill="freeze" />
                            <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                          </line>
                          <line x1="125" y1="125" x2="30" y2="125" stroke="#00ffc8" strokeWidth="3.5" opacity="0.7">
                            <animate attributeName="x2" from="125" to="30" dur="0.7s" fill="freeze" />
                            <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                          </line>
                        </g>
                      </g>
                    </svg>
                    <FaCheckCircle className="success-icon" />
                  </div>
                  <span className="success-msg">Enviado com sucesso!<br/>Aguarde a ligação da Bianca.</span>
                </SuccessExplosionBig>
              ) : (
                <ErrorExplosionBig>
                  <div className="explosion">
                    <svg className="explosion-svg" viewBox="0 0 250 250">
                      <g>
                        <circle cx="125" cy="125" r="90" stroke="#F22F46" strokeWidth="4" fill="none" opacity="0.4">
                          <animate attributeName="r" from="40" to="90" dur="0.7s" fill="freeze" />
                          <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                        </circle>
                        <circle cx="125" cy="125" r="60" stroke="#F22F46" strokeWidth="2.8" fill="none" opacity="0.3">
                          <animate attributeName="r" from="18" to="60" dur="1s" fill="freeze" />
                          <animate attributeName="opacity" from="1" to="0.2" dur="1.2s" fill="freeze" />
                        </circle>
                        <g>
                          <line x1="125" y1="125" x2="125" y2="30" stroke="#F22F46" strokeWidth="3.5" opacity="0.7">
                            <animate attributeName="y2" from="125" to="30" dur="0.7s" fill="freeze" />
                            <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                          </line>
                          <line x1="125" y1="125" x2="220" y2="125" stroke="#F22F46" strokeWidth="3.5" opacity="0.7">
                            <animate attributeName="x2" from="125" to="220" dur="0.7s" fill="freeze" />
                            <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                          </line>
                          <line x1="125" y1="125" x2="125" y2="220" stroke="#F22F46" strokeWidth="3.5" opacity="0.7">
                            <animate attributeName="y2" from="125" to="220" dur="0.7s" fill="freeze" />
                            <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                          </line>
                          <line x1="125" y1="125" x2="30" y2="125" stroke="#F22F46" strokeWidth="3.5" opacity="0.7">
                            <animate attributeName="x2" from="125" to="30" dur="0.7s" fill="freeze" />
                            <animate attributeName="opacity" from="1" to="0.1" dur="1.2s" fill="freeze" />
                          </line>
                        </g>
                      </g>
                    </svg>
                    <FaCheckCircle className="success-icon fail" style={{ color: '#F22F46', filter: 'drop-shadow(0 0 12px #F22F46)' }} />
                  </div>
                  <span className="success-msg fail">Não foi possível processar o link.<br/>Confira as informações e tente novamente.</span>
                </ErrorExplosionBig>
              )}
            </ModalOverlay>
          )}
        </Form>
      </ContentContainer>
    </HeroContainer>
  );
}
